import React from 'react';
import {AttributionControl, Layer, Map as MapGL, MapRef, Source} from 'react-map-gl/mapbox';
import 'mapbox-gl/dist/mapbox-gl.css';

import {EvalSite} from 'src/lib/api/eval';
import type {InteractiveMouseEvent} from 'src/lib/map';
import {MAPBOX_ACCESS_TOKEN} from 'src/lib/map';

type Props = {
  sites: Array<
    EvalSite & {
      _metricValue?: number | null;
      _metricColor?: string;
    }
  >;
  onSiteClick?: (siteId: string) => void;
  highlightedSiteId?: string | null;
};

const SOURCE_ID = 'eval-sites';
const LAYER_ID = 'eval-sites-circles';
const HIGHLIGHT_LAYER_ID = 'eval-sites-highlight';

type MapMode = 'interactive' | 'flat';

export default function EvalOverviewMap({sites, onSiteClick, highlightedSiteId}: Props) {
  const mapRef = React.useRef<MapRef>(null);
  const [mode, setMode] = React.useState<MapMode>('interactive');
  const [hovering, setHovering] = React.useState(false);

  const sitesData = React.useMemo<GeoJSON.FeatureCollection>(
    () => ({
      type: 'FeatureCollection',
      features: sites
        .filter((s) => Number.isFinite(s.lat) && Number.isFinite(s.lon))
        .map((s) => ({
          type: 'Feature',
          geometry: {type: 'Point', coordinates: [s.lon, s.lat]},
          properties: {
            siteId: s.siteId,
            displayName: s.displayName,
            metric: s._metricValue ?? null,
            color: s._metricColor ?? '#2563eb',
          },
        })),
    }),
    [sites]
  );

  const interactive = mode === 'interactive';

  const handleModeChange = (m: MapMode) => {
    setMode(m);
    const map = mapRef.current?.getMap();
    if (!map) return;
    if (m === 'flat') {
      map.setProjection('equalEarth');
      map.easeTo({center: [0, 10], zoom: 0.3, duration: 600});
    } else {
      map.setProjection('mercator');
      map.easeTo({center: [-98, 39], zoom: 3, duration: 600});
    }
  };

  // Fly to the highlighted site
  React.useEffect(() => {
    if (!highlightedSiteId || mode === 'flat') return;
    const site = sites.find((s) => s.siteId === highlightedSiteId);
    if (!site || !Number.isFinite(site.lat) || !Number.isFinite(site.lon)) return;

    mapRef.current?.easeTo({center: [site.lon, site.lat], duration: 500});
  }, [highlightedSiteId, sites, mode]);

  const handleClick = React.useCallback(
    (e: InteractiveMouseEvent) => {
      const siteId = e.features?.[0]?.properties?.siteId;
      if (siteId && onSiteClick) onSiteClick(siteId);
    },
    [onSiteClick]
  );

  return (
    <div className="relative w-full h-[420px] lg:h-full">
      <div className="w-full h-full rounded-md border border-gray-200 overflow-hidden">
        <MapGL
          ref={mapRef}
          mapboxAccessToken={MAPBOX_ACCESS_TOKEN}
          mapStyle="mapbox://styles/mapbox/light-v11"
          initialViewState={{longitude: -98, latitude: 39, zoom: 3}}
          attributionControl={false}
          interactiveLayerIds={[LAYER_ID]}
          cursor={hovering ? 'pointer' : 'auto'}
          onClick={handleClick}
          onMouseMove={(e: InteractiveMouseEvent) => setHovering(!!e.features?.length)}
          onMouseLeave={() => setHovering(false)}
          dragPan={interactive}
          scrollZoom={interactive}
          doubleClickZoom={interactive}
          style={{width: '100%', height: '100%'}}
        >
          <AttributionControl compact position="bottom-right" />
          <Source id={SOURCE_ID} type="geojson" data={sitesData}>
            <Layer
              id={LAYER_ID}
              type="circle"
              paint={{
                'circle-radius': 7,
                'circle-color': ['get', 'color'],
                'circle-stroke-color': '#1e3a8a',
                'circle-stroke-width': 2,
              }}
            />
            {/* Highlight ring for hovered site */}
            <Layer
              id={HIGHLIGHT_LAYER_ID}
              type="circle"
              filter={['==', ['get', 'siteId'], highlightedSiteId ?? '']}
              paint={{
                'circle-radius': 12,
                'circle-color': 'transparent',
                'circle-stroke-color': '#2563eb',
                'circle-stroke-width': 3,
              }}
            />
          </Source>
        </MapGL>
      </div>
      <div className="absolute top-2 left-2 flex rounded-md overflow-hidden border border-gray-300 shadow-sm text-xs">
        <button
          className={`px-2 py-1 ${mode === 'interactive' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          onClick={() => handleModeChange('interactive')}
        >
          Region
        </button>
        <button
          className={`px-2 py-1 ${mode === 'flat' ? 'bg-blue-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
          onClick={() => handleModeChange('flat')}
        >
          Global
        </button>
      </div>
    </div>
  );
}
